/**
 * Port of app/lib/scenario.py — the "which purchase strategy wins" layer.
 *
 * Errors stay values rather than exceptions at this boundary: a strategy whose
 * mortgage is unaffordable still returns a Scenario, carrying an
 * ErroredMortgageLifetime, so a partially failing comparison still renders.
 */

import type { Appreciator } from './appreciation.ts';
import type { Mortgage } from './mortgage.ts';
import {
    UnendingMortgageError,
    length,
    totalCost,
    totalInterest,
} from './mortgage.ts';
import type { MortgageDetail } from './mortgage_detail.ts';
import { TooHighLoanToValueError } from './mortgage_detail.ts';

export interface MortgageLifetime {
    deposit: number;
    principal: number;
    house_price: number;
    house_price_at_end: number;
    appreciation_rate: number;
    monthly_cost: number;
    term: number;
    total_cost: number;
    loan_cost: number;
    mortgage_detail: MortgageDetail;
    /**
     * The payments the figures above were summed from, kept so the page can
     * show the schedule underneath a case. Carried rather than recomputed in
     * the view: a second call would be a second calculation to keep in step
     * with this one.
     */
    mortgage: Mortgage;
}

export interface ErroredMortgageLifetime {
    deposit: number;
    principal: number;
    house_price: number;
    appreciation_rate: number;
    monthly_cost: number;
    mortgage_detail: MortgageDetail | null;
    error: string;
    error_message: string;
}

export type AnyMortgageLifetime = MortgageLifetime | ErroredMortgageLifetime;

export interface Scenario {
    equity: number | null;
    total_months: number | null;
    mortgage_lifetimes: AnyMortgageLifetime[];
}

export interface ScenarioComparison {
    /** Positive when the alternative pays less interest than the base. */
    interest_saved: number;
    /** Positive when the alternative clears the debt sooner. */
    months_saved: number;
}

export interface ScenarioContext {
    house_price: number;
    deposit: number;
    monthly_payment: number;
}

export interface EconomyContext {
    for_monthly_cost: (
        detail: MortgageDetail,
        monthly_charge: number,
        principle: number,
    ) => Mortgage;
    appreciator: Appreciator;
    get_best_mortgage_detail: (
        deposit: number,
        house_price: number,
    ) => MortgageDetail;
}

export function isErrored(
    mortgage_lifetime: AnyMortgageLifetime,
): mortgage_lifetime is ErroredMortgageLifetime {
    return (mortgage_lifetime as ErroredMortgageLifetime).error !== undefined;
}

/** Interest across every mortgage in the scenario, null if any of them failed. */
export function totalLoanCost(scenario: Scenario): number | null {
    let total = 0;
    for (const mortgage_lifetime of scenario.mortgage_lifetimes) {
        if (isErrored(mortgage_lifetime)) {
            return null;
        }
        total += mortgage_lifetime.loan_cost;
    }
    return total;
}

/**
 * What an alternative saves against the base case.
 *
 * Null when either side failed rather than 0 or Infinity: an errored scenario
 * has no term and no interest, so there is nothing to subtract, and the views
 * leave the comparison out instead of printing a number they made up.
 */
export function compareScenarios(
    base: Scenario,
    alternative: Scenario,
): ScenarioComparison | null {
    const base_cost = totalLoanCost(base);
    const alternative_cost = totalLoanCost(alternative);

    if (
        base_cost === null ||
        alternative_cost === null ||
        base.total_months === null ||
        alternative.total_months === null
    ) {
        return null;
    }

    return {
        interest_saved: base_cost - alternative_cost,
        months_saved: base.total_months - alternative.total_months,
    };
}

export interface Horizon {
    /** Months from the start: the date every case is judged at. */
    months: number;
    /** The deposit you were going to put down anyway. */
    deposit: number;
    /** The payment you were going to make anyway, every month. */
    monthly_payment: number;
}

export interface NetWorth {
    /** The house, carried on from the case's own finish to the horizon. */
    house: number;
    /**
     * Deposit the case did not ask for, still in your pocket — and negative
     * for a case that wanted more than the wallet, because that is money you
     * had to find rather than money you kept.
     */
    deposit_kept: number;
    /**
     * Budget not spent while the mortgage ran — **negative** for a case that
     * repays more each month than you were going to, because that money is
     * gone out of the same wallet.
     */
    budget_kept_paying: number;
    /** Budget not spent after the mortgage ended, up to the horizon. */
    budget_kept_after: number;
    total: number;
}

/**
 * What a case leaves you holding at a date every case is judged at.
 *
 * Comparing what each case owns at its own finish is not a comparison at all:
 * the one that clears the debt first shows the smallest house, having had the
 * least time to appreciate, which makes finishing early look like a loss. So
 * the house is carried on to the common horizon, and the money the case did
 * not need is added back — both the deposit it did not ask for and the budget
 * it stopped spending once the mortgage was gone.
 *
 * Everyone is given the same wallet, `horizon.deposit` up front and
 * `horizon.monthly_payment` a month, so a case cannot look good by quietly
 * spending more. Cash left over is assumed to sit there earning nothing, and
 * nothing here counts the rent or the living costs that are the same
 * whichever case you pick.
 *
 * The parts come back as well as the total, because a single number nobody can
 * take apart is a number nobody should trust.
 *
 * Null when the case failed: it has no term and nothing to value.
 */
export function netWorthAtHorizon(
    appreciator: Appreciator,
    scenario: Scenario,
    horizon: Horizon,
): NetWorth | null {
    if (scenario.total_months === null || scenario.equity === null) {
        return null;
    }

    let deposit = 0;
    let paid = 0;
    for (const mortgage_lifetime of scenario.mortgage_lifetimes) {
        if (isErrored(mortgage_lifetime)) {
            return null;
        }
        paid += mortgage_lifetime.total_cost;
    }
    // Only the first deposit is money out of your pocket; a later one is the
    // proceeds of selling the house before it.
    deposit = scenario.mortgage_lifetimes[0].deposit;

    const house = appreciator.appreciate(
        scenario.equity,
        horizon.months - scenario.total_months,
    );
    const deposit_kept = horizon.deposit - deposit;
    // Split rather than netted, because the two halves pull opposite ways for
    // a case that repays more each month: it spends over the budget for the
    // whole of its term and then spends none of it for the months it saved.
    // One figure would hide both.
    const budget_kept_paying =
        horizon.monthly_payment * scenario.total_months - paid;
    const budget_kept_after =
        horizon.monthly_payment * (horizon.months - scenario.total_months);

    return {
        house,
        deposit_kept,
        budget_kept_paying,
        budget_kept_after,
        total: house + deposit_kept + budget_kept_paying + budget_kept_after,
    };
}

export function getMortgageLifetime(
    economy_context: EconomyContext,
    context: ScenarioContext,
): AnyMortgageLifetime {
    const { house_price, deposit, monthly_payment } = context;
    const appreciator = economy_context.appreciator;
    const principal = house_price - deposit;

    let mortgage_detail: MortgageDetail;
    try {
        mortgage_detail = economy_context.get_best_mortgage_detail(
            deposit,
            house_price,
        );
    } catch (error) {
        if (error instanceof TooHighLoanToValueError) {
            return {
                deposit,
                principal,
                house_price,
                appreciation_rate: appreciator.appreciation_rate,
                monthly_cost: monthly_payment,
                mortgage_detail: null,
                error: 'TooHighLoanToValueError',
                error_message: error.message,
            };
        }
        throw error;
    }

    try {
        const mortgage = economy_context.for_monthly_cost(
            mortgage_detail,
            monthly_payment,
            principal,
        );
        const total_months = length(mortgage);

        return {
            deposit,
            principal,
            house_price,
            house_price_at_end: appreciator.appreciate(
                house_price,
                total_months,
            ),
            appreciation_rate: appreciator.appreciation_rate,
            monthly_cost: monthly_payment,
            term: total_months,
            total_cost: totalCost(mortgage),
            loan_cost: totalInterest(mortgage),
            mortgage_detail,
            mortgage,
        };
    } catch (error) {
        if (error instanceof UnendingMortgageError) {
            return {
                deposit,
                principal,
                house_price,
                appreciation_rate: appreciator.appreciation_rate,
                monthly_cost: monthly_payment,
                mortgage_detail,
                error: 'UnendingMortgageError',
                error_message: error.message,
            };
        }
        throw error;
    }
}

export function simplePurchase(
    economy_context: EconomyContext,
    scenario_context: ScenarioContext,
): Scenario {
    const mortgage_lifetime = getMortgageLifetime(
        economy_context,
        scenario_context,
    );

    if (isErrored(mortgage_lifetime)) {
        return {
            equity: null,
            total_months: null,
            mortgage_lifetimes: [mortgage_lifetime],
        };
    }

    return {
        equity: mortgage_lifetime.house_price_at_end,
        total_months: mortgage_lifetime.term,
        mortgage_lifetimes: [mortgage_lifetime],
    };
}

export function halfStepPurchase(
    economy_context: EconomyContext,
    scenario_context: ScenarioContext,
): Scenario {
    // Python floor-divides here; JS division does not.
    const first_house_price = Math.floor(
        (scenario_context.deposit + scenario_context.house_price) / 2,
    );

    const first_mortgage_lifetime = getMortgageLifetime(economy_context, {
        house_price: first_house_price,
        deposit: scenario_context.deposit,
        monthly_payment: scenario_context.monthly_payment,
    });

    if (isErrored(first_mortgage_lifetime)) {
        return {
            equity: null,
            total_months: null,
            mortgage_lifetimes: [first_mortgage_lifetime],
        };
    }

    const appreciated_first_house_price =
        first_mortgage_lifetime.house_price_at_end;
    const appreciated_final_house_price =
        economy_context.appreciator.appreciate(
            scenario_context.house_price,
            first_mortgage_lifetime.term,
        );

    const final_mortgage_lifetime = getMortgageLifetime(economy_context, {
        house_price: appreciated_final_house_price,
        deposit: appreciated_first_house_price,
        monthly_payment: scenario_context.monthly_payment,
    });

    if (isErrored(final_mortgage_lifetime)) {
        return {
            equity: null,
            total_months: null,
            mortgage_lifetimes: [
                first_mortgage_lifetime,
                final_mortgage_lifetime,
            ],
        };
    }

    return {
        equity: final_mortgage_lifetime.house_price_at_end,
        total_months:
            first_mortgage_lifetime.term + final_mortgage_lifetime.term,
        mortgage_lifetimes: [first_mortgage_lifetime, final_mortgage_lifetime],
    };
}
